/**
 * LE RÉSULTAT DES JOURNÉES, CÔTÉ ÉCRAN — 17/09/2026, lot C.
 *
 * Maquette `docs/maquettes/journee-resultat.html`, validée le 17/09.
 *
 * CE FICHIER NE CALCULE PAS D'ARGENT, IL LE REGROUPE. Le chiffre d'affaires est
 * réparti par l'API (`resultat.ts`), les dépenses y sont chiffrées
 * (`cout_journee.ts`). Ici on additionne des montants déjà décidés, en CENTIMES
 * ENTIERS, et on les range par jour, par attelage ou par service.
 *
 * REFAIRE UN CALCUL ICI SERAIT EN TENIR DEUX. C'est la règle du dépôt depuis le
 * mètre linéaire : ce qui doit être vrai des deux côtés vit dans `@stafa/core`,
 * ce qui n'appartient qu'à l'un reste chez lui.
 */

/** Une journée telle que l'API la rend. */
export interface JourneeResultat {
  readonly vehicule_id: string;
  readonly jour: string;
  /**
   * `null` QUAND LE CHIFFRE D'AFFAIRES NE SE RÉPARTIT PAS — 30/10/2026.
   * `pourquoi_sans_ca` dit alors ce qu'il manque. Zéro se lirait « cette
   * journée n'a rien rapporté », ce qui est faux.
   */
  readonly ca_centimes: number | null;
  readonly pourquoi_sans_ca: string | null;
  readonly resultat_centimes: number | null;
  readonly km: number | null;
  /** Les kilomètres SAISIS, même sur une journée inachevée. Toujours un nombre. */
  readonly km_releves: number;
  readonly trajets_manquants: number;
  readonly peage_centimes: number;
  readonly immatriculation: string;
  readonly chauffeurs: string | null;
  readonly remorques: string | null;
  readonly depenses: DepensesResultat | null;
  readonly ordres: readonly OrdreDeJournee[];
}

/** La part d'un centre de profit dans le coût d'une journée (30/10/2026). */
export interface PartDeCentreLue {
  readonly centre_id: string;
  readonly nom: string;
  readonly part: number;
  readonly cout_journalier_centimes: number;
}

export interface DepensesResultat {
  /**
   * LA JOURNÉE D'ATTELAGE, un seul poste depuis le 30/10/2026 — il REMPLACE
   * `tracteur`, `remorque`, `conducteur` et `structure`. Le coût journalier se
   * saisit maintenant d'un bloc par centre de profit, et le redécouper ici
   * serait inventer une ventilation que personne n'a donnée.
   */
  readonly journee: number;
  /** D'où vient `journee`, centre par centre, pour le dépliant « Le calcul ». */
  readonly parts: readonly PartDeCentreLue[];
  readonly gazole: number;
  readonly frais_km: number;
  readonly frais_heure: number;
  readonly peage: number;
  readonly total: number;
  readonly manques: readonly string[];
}

export interface OrdreDeJournee {
  readonly ot_id: string;
  readonly numero: string;
  readonly client_nom: string;
  readonly centre_id: string;
  readonly centre_nom: string;
  readonly km: number;
  readonly ca_centimes: number;
  readonly sans_prix: boolean;
}

export interface JourCreuxResultat {
  readonly vehicule_id: string;
  readonly immatriculation: string;
  readonly jour: string;
  readonly cout_centimes: number;
  readonly manques: readonly string[];
}

export interface Incalculable {
  readonly vehicule_id: string;
  readonly jour: string;
  readonly pourquoi: string;
}

export interface ResultatLu {
  readonly journees: readonly JourneeResultat[];
  readonly creux: readonly JourCreuxResultat[];
  readonly incalculables: readonly Incalculable[];
  readonly surcharge_non_comptee: boolean;
}

// ---------------------------------------------------------------------------
// Écran 1 — par jour
// ---------------------------------------------------------------------------

export interface LigneJour {
  readonly jour: string;
  readonly camions: number;
  readonly km: number | null;
  readonly ca: number;
  readonly depenses: number;
  readonly resultat: number;
  readonly creux: number;
  readonly resultat_du_jour: number;
  /**
   * Vrai quand une journée de ce jour n'a pas pu être chiffrée. La ligne ne
   * montre alors AUCUN total : un résultat calculé sur une partie des camions
   * serait faux en paraissant complet. Même règle que `totauxDeLaJournee`, qui
   * rend `null` dès qu'un trajet manque.
   */
  readonly incomplet: boolean;
}

export function parJour(lu: ResultatLu): readonly LigneJour[] {
  const jours = new Map<
    string,
    { camions: number; km: number | null; ca: number; dep: number; creux: number }
  >();

  const prendre = (jour: string) => {
    const connu = jours.get(jour);
    if (connu !== undefined) return connu;
    const neuf = { camions: 0, km: 0 as number | null, ca: 0, dep: 0, creux: 0 };
    jours.set(jour, neuf);
    return neuf;
  };

  for (const j of lu.journees) {
    const p = prendre(j.jour);
    p.camions += 1;
    p.ca += j.ca_centimes ?? 0;
    p.dep += j.depenses?.total ?? 0;
    p.km = p.km === null || j.km === null ? null : p.km + j.km;
  }
  for (const c of lu.creux) prendre(c.jour).creux += c.cout_centimes;

  const troues = new Set(lu.incalculables.map((i) => i.jour));

  return [...jours.entries()]
    .map(([jour, p]) => ({
      jour,
      camions: p.camions,
      km: p.km,
      ca: p.ca,
      depenses: p.dep,
      resultat: p.ca - p.dep,
      creux: p.creux,
      resultat_du_jour: p.ca - p.dep - p.creux,
      incomplet: troues.has(jour),
    }))
    .sort((a, b) => (a.jour === b.jour ? 0 : a.jour < b.jour ? -1 : 1));
}

/** Le bandeau de totaux, en haut de l'écran. */
export interface TotauxPeriode {
  readonly ca: number;
  readonly depenses: number;
  readonly creux: number;
  /** Chiffre d'affaires moins dépenses, SANS les jours creux. */
  readonly resultat_journees: number;
  /** Le vrai résultat : jours creux déduits. C'est le chiffre qu'on vient chercher. */
  readonly resultat: number;
  readonly km: number | null;
}

/**
 * LES TOTAUX DE LA PÉRIODE, SOMMÉS SUR LES LIGNES DÉJÀ AFFICHÉES.
 *
 * Et non recalculés depuis `lu` : le bandeau doit dire exactement ce que la
 * colonne du dessous additionne. Deux chemins de calcul, c'est deux chances
 * d'afficher un total qui ne correspond à aucune addition visible — et c'est
 * l'utilisateur qui le découvre, une calculatrice à la main.
 *
 * LES KILOMÈTRES SONT INCONNUS DÈS QU'UN SEUL JOUR LES IGNORE. Un total
 * partiel se lirait comme la vérité du mois. Même règle que `parJour`.
 */
export function totauxDeLaPeriode(jours: readonly LigneJour[]): TotauxPeriode {
  let ca = 0;
  let depenses = 0;
  let creux = 0;
  let km: number | null = 0;
  for (const j of jours) {
    ca += j.ca;
    depenses += j.depenses;
    creux += j.creux;
    km = km === null || j.km === null ? null : km + j.km;
  }
  return {
    ca,
    depenses,
    creux,
    resultat_journees: ca - depenses,
    resultat: ca - depenses - creux,
    km,
  };
}

// ---------------------------------------------------------------------------
// Écran 4 — par attelage
// ---------------------------------------------------------------------------

/** Les trois lectures de l'écran 4. Ce sont trois questions différentes. */
export type Regroupement = 'attelage' | 'tracteur' | 'conducteur';

export interface LigneAttelage {
  readonly cle: string;
  readonly nom: string;
  readonly jours_roules: number;
  readonly jours_creux: number;
  readonly km: number;
  readonly ca: number;
  readonly depenses: number;
  readonly resultat: number;
  readonly par_jour_roule: number;
  /** Le coût du kilomètre, en CENTIMES, ou `null` sans kilomètre. */
  readonly cout_km: number | null;
}

/**
 * LE REGROUPEMENT NE COUPE PAS LA JOURNÉE.
 *
 * La décision du 13/09 tient : « une ligne = un véhicule, pas un triplet ; un
 * échange de remorque ne coupe pas la journée ». Ce qu'on fait ici, c'est
 * REGROUPER des journées entières, jamais les découper. Une journée où la
 * remorque a changé porte les deux dans son nom, et reste UNE journée.
 */
export function parAttelage(
  lu: ResultatLu,
  comment: Regroupement,
): readonly LigneAttelage[] {
  const nomDe = (j: JourneeResultat): string => {
    if (comment === 'tracteur') return j.immatriculation;
    if (comment === 'conducteur') return j.chauffeurs ?? 'sans conducteur';
    const remorque = j.remorques === null ? '' : ` + ${j.remorques}`;
    const qui = j.chauffeurs === null ? '' : ` · ${j.chauffeurs}`;
    return `${j.immatriculation}${remorque}${qui}`;
  };

  const lignes = new Map<
    string,
    { nom: string; roules: number; km: number; ca: number; dep: number }
  >();
  for (const j of lu.journees) {
    const nom = nomDe(j);
    const connu = lignes.get(nom);
    const p = connu ?? { nom, roules: 0, km: 0, ca: 0, dep: 0 };
    if (connu === undefined) lignes.set(nom, p);
    p.roules += 1;
    p.km += j.km ?? 0;
    p.ca += j.ca_centimes ?? 0;
    p.dep += j.depenses?.total ?? 0;
  }

  /**
   * LES JOURS CREUX NE SE RATTACHENT QU'AU TRACTEUR. Un camion à quai n'a pas
   * d'équipage ce jour-là : lui en inventer un pour faire une ligne serait
   * fabriquer un attelage qui n'a pas existé. Ils comptent donc sur
   * l'immatriculation, quel que soit le regroupement demandé.
   */
  const creuxParImmat = new Map<string, { jours: number; cout: number }>();
  for (const c of lu.creux) {
    const connu = creuxParImmat.get(c.immatriculation);
    const p = connu ?? { jours: 0, cout: 0 };
    if (connu === undefined) creuxParImmat.set(c.immatriculation, p);
    p.jours += 1;
    p.cout += c.cout_centimes;
  }

  return [...lignes.values()]
    .map((p) => {
      const creux =
        comment === 'conducteur'
          ? { jours: 0, cout: 0 }
          : creuxParImmat.get(comment === 'tracteur' ? p.nom : p.nom.split(' ')[0] ?? '') ?? {
              jours: 0,
              cout: 0,
            };
      const depenses = p.dep + creux.cout;
      return {
        cle: p.nom,
        nom: p.nom,
        jours_roules: p.roules,
        jours_creux: creux.jours,
        km: p.km,
        ca: p.ca,
        depenses,
        resultat: p.ca - depenses,
        par_jour_roule: p.roules === 0 ? 0 : Math.round((p.ca - depenses) / p.roules),
        cout_km: p.km === 0 ? null : Math.round(depenses / p.km),
      };
    })
    .sort((a, b) => b.resultat - a.resultat);
}

// ---------------------------------------------------------------------------
// Écran 5 — par service
// ---------------------------------------------------------------------------

export interface LigneService {
  readonly centre_id: string;
  readonly nom: string;
  readonly ordres: number;
  readonly km: number;
  readonly ca: number;
  readonly depenses: number;
  readonly resultat: number;
}

/**
 * LE RÉSULTAT PAR SERVICE — le centre de profit de l'ordre.
 *
 * LA DÉPENSE D'UNE JOURNÉE SE PARTAGE ENTRE SES ORDRES AU PRORATA DES
 * KILOMÈTRES FAITS À BORD, et c'est la troisième répartition de ce chantier.
 * Elle mérite d'être dite :
 *
 *   - AU PRORATA DES KILOMÈTRES parce que c'est ce qui use le camion. Un ordre
 *     qui roule 400 km coûte plus qu'un ordre qui en roule 40, et le partager
 *     au prorata du CA reviendrait à décider d'avance que tous les ordres d'une
 *     journée ont la même marge — c'est-à-dire à ne rien mesurer du tout ;
 *   - LES KILOMÈTRES D'APPROCHE ET DE RETOUR N'APPARTIENNENT À AUCUN ORDRE,
 *     mais ils ont coûté. Ils sont donc partagés AVEC le reste : ce qu'on
 *     répartit, c'est la dépense ENTIÈRE de la journée, sur la seule clé qu'on
 *     ait — les kilomètres à bord ;
 *   - QUAND AUCUN ORDRE N'A ROULÉ à bord — une journée de prestations sur
 *     place — la dépense se partage À PARTS ÉGALES. Le prorata de rien n'existe,
 *     et laisser la dépense de côté la ferait disparaître du résultat.
 *
 * LES JOURS CREUX N'ENTRENT DANS AUCUN SERVICE, décision du 17/09 : un camion à
 * quai ne travaille pour personne. Les répartir au prorata du CA punirait le
 * service le plus gros pour une inactivité qu'il n'a pas causée.
 */
export function parService(lu: ResultatLu): readonly LigneService[] {
  const services = new Map<
    string,
    { nom: string; ordres: Set<string>; km: number; ca: number; dep: number }
  >();

  for (const j of lu.journees) {
    const total = j.depenses?.total ?? 0;
    const kmABord = j.ordres.reduce((s, o) => s + o.km, 0);

    j.ordres.forEach((o, i) => {
      const connu = services.get(o.centre_id);
      const p = connu ?? {
        nom: o.centre_nom,
        ordres: new Set<string>(),
        km: 0,
        ca: 0,
        dep: 0,
      };
      if (connu === undefined) services.set(o.centre_id, p);
      p.ordres.add(o.ot_id);
      p.km += o.km;
      p.ca += o.ca_centimes;

      /**
       * LE RELIQUAT VA AU DERNIER, pour que la somme des parts fasse exactement
       * la dépense de la journée. Sans cela, un centime s'évaporerait à chaque
       * journée à trois ordres, et les services ne retomberaient jamais sur le
       * total du mois.
       */
      const dernier = i === j.ordres.length - 1;
      if (dernier) {
        const dejaParti = j.ordres
          .slice(0, i)
          .reduce(
            (s, autre) =>
              s +
              (kmABord > 0
                ? Math.round((total * autre.km) / kmABord)
                : Math.round(total / j.ordres.length)),
            0,
          );
        p.dep += total - dejaParti;
      } else {
        p.dep +=
          kmABord > 0
            ? Math.round((total * o.km) / kmABord)
            : Math.round(total / j.ordres.length);
      }
    });
  }

  return [...services.entries()]
    .map(([centre_id, p]) => ({
      centre_id,
      nom: p.nom,
      ordres: p.ordres.size,
      km: p.km,
      ca: p.ca,
      depenses: p.dep,
      resultat: p.ca - p.dep,
    }))
    .sort((a, b) => b.ca - a.ca);
}

// ---------------------------------------------------------------------------
// Ce qui s'affiche
// ---------------------------------------------------------------------------

/** Un montant en centimes, écrit en français. `null` rend un tiret cadratin. */
export function euros(centimes: number | null | undefined): string {
  if (centimes === null || centimes === undefined) return '—';
  const signe = centimes < 0 ? '−' : '';
  const abs = Math.abs(centimes);
  const entier = String(Math.floor(abs / 100));
  const cents = String(abs % 100).padStart(2, '0');
  const milliers = entier.replace(/\B(?=(\d{3})+(?!\d))/gu, ' ');
  return `${signe}${milliers},${cents}`;
}

/** Un taux de marge, en pour-cent à une décimale. */
export function marge(ca: number, resultat: number): string {
  if (ca === 0) return '—';
  const taux = (resultat / ca) * 100;
  const signe = taux < 0 ? '−' : '';
  return `${signe}${Math.abs(taux).toFixed(1).replace('.', ',')} %`;
}

/** Un nombre de kilomètres, espace insécable aux milliers. */
export function km(valeur: number | null | undefined): string {
  if (valeur === null || valeur === undefined) return '—';
  return String(valeur).replace(/\B(?=(\d{3})+(?!\d))/gu, ' ');
}

/** Le jour, en français court : « mardi 15 ». */
export function jourDit(jour: string): string {
  const jours = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
  const d = new Date(`${jour}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return jour;
  return `${jours[d.getUTCDay()] ?? ''} ${String(d.getUTCDate())}`;
}
